import React, { useMemo, useState, useEffect } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

function Table({
  columns,
  data,
  filterInput,
  setFilterInput,
  pageCount,
  loading,
  handleEdit,
  handleDelete,
  onPageChange,
  currentPage,
  actionButtonLabel = "Add",
  showActionButton = false,
  onTrigger = () => {},
  onImportTrigger = () => {},
  className = "", // Accept className as a prop with a default empty string
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [screenSize, setScreenSize] = useState(() => {
    const width = window.innerWidth;
    if (width < 320) return 'xs';
    if (width < 480) return 'sm';
    if (width < 640) return 'md';
    if (width < 768) return 'lg';
    if (width < 1024) return 'xl';
    if (width < 1280) return '2xl';
    if (width < 1920) return '3xl';
    return '4xl';
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      
      if (width < 320) setScreenSize('xs');
      else if (width < 480) setScreenSize('sm');
      else if (width < 640) setScreenSize('md');
      else if (width < 768) setScreenSize('lg');
      else if (width < 1024) setScreenSize('xl');
      else if (width < 1280) setScreenSize('2xl');
      else if (width < 1920) setScreenSize('3xl');
      else setScreenSize('4xl');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredData = useMemo(() => {
    if (!filterInput) return data; // No filtering if there's no input
    return data.filter((row) =>
      Object.values(row).some(
        (value) =>
          String(value).toLowerCase().includes(filterInput.toLowerCase())
      )
    );
  }, [data, filterInput]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 10 },
      pageCount,
      manualPagination: true,
      sortBy: [{ id: 'name', desc: true }],
    },
    useSortBy,
    usePagination
  );

  // Call the onPageChange when the page changes
  const handleGotoPage = (pageIndex) => {
    gotoPage(pageIndex);
    onPageChange(pageIndex); // Trigger the page change in the parent
  };

  return (
    <div className="applications">
      <div className={`table-controls ${isMobile ? 'mobile-controls' : ''}`}>
        {showActionButton && actionButtonLabel === 'Add Community' && (
          <button
            type="button"
            onClick={onImportTrigger}
            className="btn btn-add-comm"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {isMobile ? 'Import' : 'Import Communities'}
          </button>
        )}
        {showActionButton && (
          <button
            type="button"
            onClick={onTrigger}
            className="btn btn-add-comm"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {isMobile && actionButtonLabel !== 'Add Community' ? 'Add' : actionButtonLabel}
          </button>
        )}
        {data.length > 0 && (
          <div
            className="search-wrapper"
          >
            <input
              type="text"
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              className="form-control search-input"
              placeholder="Search applications..."
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="search-icon"
            />
            {filterInput && (
              <button
                type="button"
                onClick={() => setFilterInput("")}
                className="clear-search-btn"
                title="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="outer">
        {isMobile ? (
          // Mobile Card View
          <div className="mobile-cards-container">
            {loading ? (
              <div className="loading-state">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading applications...</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faSearch} size="3x" className="mb-3 text-muted" />
                <h5>No applications found</h5>
                <p className="text-muted">
                  {filterInput ? "Try adjusting your search terms" : "No applications available"}
                </p>
              </div>
            ) : (
              page.map((row, rowIndex) => {
                prepareRow(row);
                return (
                  <div key={row.id || rowIndex} className="application-card">
                    {row.cells
                      .filter(cell => cell.column.id !== 'actions')
                      .map((cell, cellIndex) => {
                        const header = cell.column.Header;
                        return (
                          <div key={cellIndex} className="card-row">
                            <div className="card-label">{header}:</div>
                            <div className="card-value">{cell.render('Cell')}</div>
                          </div>
                        );
                      })}
                    <div className="card-actions">
                      {row.cells
                        .find(cell => cell.column.id === 'actions')
                        ?.render('Cell')}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          // Desktop Table View
          <table
            {...getTableProps()}
            className={`table table-striped table-bordered ${className}`}
          >
            <thead>
              {headerGroups.map((headerGroup, headerIndex) => {
                const headerGroupProps = headerGroup.getHeaderGroupProps();
                const { key: headerGroupKey, ...restHeaderGroupProps } = headerGroupProps;

                return (
                  <tr key={headerGroupKey} {...restHeaderGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const columnProps = column.getHeaderProps(column.getSortByToggleProps());
                      const { key: columnKey, ...restColumnProps } = columnProps;

                      return (
                        <th key={columnKey} {...restColumnProps}>
                          {column.render("Header")}
                          {!column.disableSortBy && (
                            <span className="sort-icon">
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FontAwesomeIcon icon={faSortDown} />
                                ) : (
                                  <FontAwesomeIcon icon={faSortUp} />
                                )
                              ) : (
                                <FontAwesomeIcon icon={faSort} />
                              )}
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>

            <tbody {...getTableBodyProps()}>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-5">
                    <div className="d-flex flex-column align-items-center">
                      <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted">Loading applications...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-5">
                    <FontAwesomeIcon icon={faSearch} size="2x" className="mb-3 text-muted" />
                    <h5>No applications found</h5>
                    <p className="text-muted">
                      {filterInput ? "Try adjusting your search terms" : "No applications available"}
                    </p>
                  </td>
                </tr>
              ) : (
                page.map((row, rowIndex) => {
                  prepareRow(row);
                  const rowProps = row.getRowProps();
                  const { key: rowKey, ...restRowProps } = rowProps;

                  return (
                    <tr key={rowKey} {...restRowProps}>
                      {row.cells.map((cell, cellIndex) => {
                        const cellProps = cell.getCellProps();
                        const { key: cellKey, ...restCellProps } = cellProps;

                        return (
                          <td key={cellKey} {...restCellProps}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {filteredData.length > 0 && (
          <div className={`pagination-wrapper ${isMobile ? 'mobile-pagination' : ''}`}>
            <div className="pagination-info">
              Showing page {currentPage + 1} of {pageCount || 1}
            </div>
            <div className="pagination">
              <button
                onClick={() => {
                  if (currentPage > 0) {
                    handleGotoPage(currentPage - 1);
                  }
                }}
                disabled={currentPage === 0}
                className="btn btn-pagination"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="pagination-numbers">
                {pageCount <= 7 ? (
                  // Show all pages if 7 or fewer
                  Array.from({ length: pageCount }, (_, index) => (
                    <button
                      key={index}
                      onClick={() => handleGotoPage(index)}
                      className={`btn btn-pagination-number ${currentPage === index ? "active" : ""}`}
                      aria-current={currentPage === index ? "page" : undefined}
                      aria-label={`Go to page ${index + 1}`}
                    >
                      {index + 1}
                    </button>
                  ))
                ) : (
                  // Show first, last, current, and adjacent pages
                  <>
                    {currentPage > 2 && (
                      <>
                        <button
                          onClick={() => handleGotoPage(0)}
                          className="btn btn-pagination-number"
                          aria-label="Go to page 1"
                        >
                          1
                        </button>
                        {currentPage > 3 && <span className="pagination-ellipsis">...</span>}
                      </>
                    )}
                    {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
                      let pageIndex;
                      if (currentPage < 3) {
                        pageIndex = i;
                      } else if (currentPage > pageCount - 4) {
                        pageIndex = pageCount - 5 + i;
                      } else {
                        pageIndex = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageIndex}
                          onClick={() => handleGotoPage(pageIndex)}
                          className={`btn btn-pagination-number ${currentPage === pageIndex ? "active" : ""}`}
                          aria-current={currentPage === pageIndex ? "page" : undefined}
                          aria-label={`Go to page ${pageIndex + 1}`}
                        >
                          {pageIndex + 1}
                        </button>
                      );
                    })}
                    {currentPage < pageCount - 3 && (
                      <>
                        {currentPage < pageCount - 4 && <span className="pagination-ellipsis">...</span>}
                        <button
                          onClick={() => handleGotoPage(pageCount - 1)}
                          className="btn btn-pagination-number"
                          aria-label={`Go to page ${pageCount}`}
                        >
                          {pageCount}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>

              <button
                onClick={() => {
                  if (currentPage < (pageCount - 1)) {
                    handleGotoPage(currentPage + 1);
                  }
                }}
                disabled={currentPage >= (pageCount - 1)}
                className="btn btn-pagination"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Table;
